import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Flex, Popconfirm } from 'antd';
import { Fragment, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  acceptTicket,
  approvalTicket,
  cancelApprovalTickets,
  cancelTicket,
  completedHandle,
  confirmComplete,
  rejectHandle
} from 'src/apis/helpdesk/ticket.api';
import Close from 'src/assets/icons/close.png';
import { HrCategoryEn } from 'src/constants/hrCategory.enum';
import urlPath from 'src/constants/urlPath';
import { AppContext } from 'src/contexts/app.context';
import { TicketDetailContext } from 'src/contexts/ticketDetail.context';
import { useBasePath } from 'src/hooks/useBasePath';
import { AddNote, AddRejectNote, AddRelatedTicket, HandleTicket } from 'src/pages/v2/Ticket/Others';
import { ApprovalStatus, TicketStatus } from 'src/types/helpdesk/ticket.type';
import { showNotification } from 'src/utils/notification';
import { isJsonType } from 'src/utils/utils';
import Icon from '../Icon';
import BtnCustom from './BtnCustom';
import './btnHandleList.scss';

export interface RequestType {
  id: string;
  isConfirm?: boolean;
  action?: ApprovalStatus;
  reasonId?: number;
  note?: string;
  stepWaiting?: number;
}

export interface RequestCancelApprovalType {
  id: string;
  step: number;
}

export interface OpenReasonType {
  isOpen: boolean;
  code?: string;
  id?: string;
  action?: ApprovalStatus;
  stepWaiting?: number;
}

export interface OpenReasonNoConfirmType {
  isOpen: boolean;
  code?: string;
  id?: string;
}

export interface OpenHandleType {
  ticketId?: string;
  ticketCode?: string;
  departmentId?: number;
  employeeId?: number;
  solutionTicketId?: number | string;
  isOpen: boolean;
}

export type ProcessType = 'approval' | 'accept' | 'handle' | 'finish';

export interface OpenNoteType {
  isOpen?: boolean;
  processType?: ProcessType;
}

export interface OpenRelatedTicketType {
  isOpen: boolean;
  ticketId?: string;
  manpowerId?: number;
  relatedTicketType?: 'overTime' | 'business' | 'expenseTrackingRequest';
}

type Props = {
  isHasRelatedTicket?: boolean;
  onClick?: () => void;
};

const BtnHandleList = ({ isHasRelatedTicket, onClick }: Props) => {
  const { ticketSelected, setTicketSelected, userInfo } = useContext(AppContext);
  const { ticket } = useContext(TicketDetailContext);
  const [openNote, setOpenNote] = useState<OpenNoteType>({ isOpen: false });
  const [openRejectNote, setOpenRejectNote] = useState<OpenNoteType>({ isOpen: false });
  const [openHandle, setOpenHandle] = useState<OpenHandleType>({
    isOpen: false,
    ticketId: undefined,
    departmentId: undefined,
    solutionTicketId: undefined,
    employeeId: undefined
  });
  const [openRelatedTicket, setOpenRelatedTicket] = useState<OpenRelatedTicketType>({
    ticketId: '',
    isOpen: false
  });
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const basePath = useBasePath();
  const handleCloseDetail = () => {
    navigate(basePath ?? '');
    setTicketSelected(undefined);
  };

  const cancelTicketMutation = useMutation({
    mutationFn: (id: string) => cancelTicket(id),
    onSuccess: (_, id) => {
      showNotification(`Hủy phiếu thành công - ${ticket?.data.ticketCode}`, 'success');
      handleRefresh();
    }
  });

  const approvalTicketsMutation = useMutation({
    mutationFn: (rq: RequestType) => approvalTicket(rq.id, rq.action, rq?.note, rq.reasonId, rq.stepWaiting),
    onSuccess: (_, rq) => {
      if (rq.action === ApprovalStatus.Approve) {
        showNotification(`Phê duyệt thành công - ${ticket?.data.ticketCode}`, 'success');
      } else if (rq.action === ApprovalStatus.Reject) {
        showNotification(`Từ chối phiếu thành công - ${ticket?.data.ticketCode}`, 'success');
      }
      handleRefresh();
    }
  });

  const cancelApprovalTicketsMutation = useMutation({
    mutationFn: (rq: RequestCancelApprovalType) => cancelApprovalTickets(rq.id, rq?.step),
    onSuccess: (_, rq) => {
      showNotification(`Thu hồi phê duyệt thành công - ${ticket?.data.ticketCode}`, 'success');
      handleRefresh();
    }
  });

  const acceptTicketsMutation = useMutation({
    mutationFn: (rq: RequestType) => acceptTicket(rq.id, rq.action, rq.reasonId, rq.note),
    onSuccess: (_, rq) => {
      if (rq.action === ApprovalStatus.Approve) {
        showNotification(`Tiếp nhận thành công - ${ticket?.data.ticketCode}`, 'success');
      } else if (rq.action === ApprovalStatus.Reject) {
        showNotification(`Từ chối tiếp nhận thành công - ${ticket?.data.ticketCode}`, 'success');
      }
      handleRefresh();
    }
  });

  const completedHandleMutation = useMutation({
    mutationFn: (rq: RequestType) => completedHandle(rq.id, rq.note),
    onSuccess: () => {
      showNotification(`Xác nhận đã xử lý thành công - ${ticket?.data.ticketCode}`, 'success');
      handleRefresh();
    }
  });

  const rejectHandleMutation = useMutation({
    mutationFn: (rq: RequestType) => rejectHandle(rq.id, userInfo?.id, rq.reasonId, rq.note),
    onSuccess: () => {
      showNotification(`Từ chối xử lý thành công - ${ticket?.data.ticketCode}`, 'success');
      handleRefresh();
    }
  });

  const confirmCompleteMutation = useMutation({
    mutationFn: (rq: RequestType) => confirmComplete(rq.id, rq.isConfirm, rq.reasonId, rq.note),
    onSuccess: (_, rq) => {
      if (rq.isConfirm) {
        showNotification(`Xác nhận hoàn tất thành công - ${ticket?.data.ticketCode}`, 'success');
      } else if (!rq.isConfirm) {
        showNotification(`Từ chối hoàn tất thành công - ${ticket?.data.ticketCode}`, 'success');
      }
      handleRefresh();
    }
  });

  const handleCancel = (id: string) => {
    cancelTicketMutation.mutate(id);
  };

  const handleCancelApproval = (id: string, step: number) => {
    if (!id && step) return;
    cancelApprovalTicketsMutation.mutate({
      id,
      step
    });
  };

  const handleAddNote = (note?: string, processType?: ProcessType) => {
    switch (processType) {
      case 'approval':
        approvalTicketsMutation.mutate({
          id: String(ticket?.data.id),
          action: ApprovalStatus.Approve,
          note: note,
          stepWaiting: ticket?.data?.stepWaiting
        });
        break;
      case 'handle':
        completedHandleMutation.mutate({
          id: String(ticket?.data.id),
          note: note
        });
        break;
      case 'finish':
        confirmCompleteMutation.mutate({ id: String(ticket?.data.id), isConfirm: true, note: note });
        break;

      default:
        break;
    }
  };

  const handleAddRejectNote = (reasonId?: number, noteReject?: string, processType?: ProcessType) => {
    switch (processType) {
      case 'approval':
        approvalTicketsMutation.mutate({
          id: String(ticket?.data.id),
          action: ApprovalStatus.Reject,
          reasonId: reasonId,
          note: noteReject,
          stepWaiting: ticket?.data?.stepWaiting
        });
        break;
      case 'accept':
        acceptTicketsMutation.mutate({
          id: String(ticket?.data.id),
          action: ApprovalStatus.Reject,
          reasonId: reasonId,
          note: noteReject
        });
        break;
      case 'handle':
        rejectHandleMutation.mutate({
          id: String(ticket?.data.id),
          reasonId: reasonId,
          note: noteReject
        });
        break;
      case 'finish':
        confirmCompleteMutation.mutate({
          id: String(ticket?.data.id),
          isConfirm: false,
          reasonId: reasonId,
          note: noteReject
        });
        break;

      default:
        break;
    }
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries(['ticket']);
  };

  const handleRenderButton = () => {
    if (!ticketSelected) {
      return <Fragment />;
    }

    //#region  My ticket
    if (basePath === urlPath.myTicket.location) {
      return (
        <Flex gap={10}>
          {ticket?.data.ticketStatusId === TicketStatus.HandleCompleted && (
            <>
              <BtnCustom
                type='satisfaction'
                onClick={() => setOpenNote({ isOpen: true, processType: 'finish' })}
                isLoading={confirmCompleteMutation.isLoading && confirmCompleteMutation.variables?.isConfirm}
              />
              <BtnCustom
                type='unsatisfaction'
                onClick={() => setOpenRejectNote({ isOpen: true, processType: 'finish' })}
                isLoading={confirmCompleteMutation.isLoading && !confirmCompleteMutation.variables?.isConfirm}
              />
            </>
          )}
          {(ticket?.data.approvalLogs.length === 0 || ticket?.data.approvalLogs === undefined) &&
            ticket?.data?.ticketStatusId === TicketStatus.New && (
              <BtnCustom type='edit' onClick={() => navigate(`${urlPath.ticketEdit.location}/${ticketSelected}`)} />
            )}
          {ticket?.data.ticketStatusId === TicketStatus.New && (
            <Popconfirm title={'Bạn chắc chắn hủy phiếu?'} onConfirm={() => handleCancel(ticket.data?.id)}>
              <BtnCustom type='cancelTicket' isLoading={cancelTicketMutation.isLoading} />
            </Popconfirm>
          )}
        </Flex>
      );
    }
    //#endregion  My ticket

    //#region  Ticket approval
    if (
      basePath === urlPath.approval.location &&
      ticket?.data.ticketStatusId === TicketStatus.New &&
      ticket?.data.isWaiting &&
      ticket?.data?.stepWaiting !== 1
    ) {
      return (
        <Flex gap={10}>
          <BtnCustom
            type='approve'
            onClick={() => setOpenNote({ isOpen: true, processType: 'approval' })}
            isLoading={
              approvalTicketsMutation.isLoading && approvalTicketsMutation.variables?.action === ApprovalStatus.Approve
            }
          />
          <BtnCustom
            type='reject'
            onClick={() => setOpenRejectNote({ isOpen: true, processType: 'approval' })}
            isLoading={
              approvalTicketsMutation.isLoading && approvalTicketsMutation.variables?.action === ApprovalStatus.Reject
            }
          />
          {ticket.data.approvalLogs[ticket.data.approvalLogs.length - 1]?.employee.id === userInfo?.id && (
            <Popconfirm
              title={'Bạn chắc chắn thu hổi phê duyệt?'}
              onConfirm={() =>
                handleCancelApproval(
                  ticket.data.id,
                  ticket.data.approvalLogs[ticket.data.approvalLogs.length - 1]?.step
                )
              }
            >
              <BtnCustom
                type='cancelApproval'
                stepCancelApproval={ticket.data.approvalLogs[ticket.data.approvalLogs.length - 1]?.step}
                isLoading={cancelApprovalTicketsMutation.isLoading}
              />
            </Popconfirm>
          )}
        </Flex>
      );
    }

    if (
      basePath === urlPath.approval.location &&
      ticket?.data.ticketStatusId === TicketStatus.New &&
      ticket?.data.isWaiting
    ) {
      return (
        <Flex gap={10}>
          <BtnCustom
            type='approve'
            onClick={() => setOpenNote({ isOpen: true, processType: 'approval' })}
            isLoading={
              approvalTicketsMutation.isLoading && approvalTicketsMutation.variables?.action === ApprovalStatus.Approve
            }
          />
          <BtnCustom
            type='reject'
            onClick={() => setOpenRejectNote({ isOpen: true, processType: 'approval' })}
            isLoading={
              approvalTicketsMutation.isLoading && approvalTicketsMutation.variables?.action === ApprovalStatus.Reject
            }
          />
        </Flex>
      );
    }

    if (
      basePath === urlPath.approval.location &&
      ticket?.data.ticketStatusId === TicketStatus.New &&
      !ticket.data?.isWaiting
    ) {
      return (
        <Flex gap={10}>
          {ticket.data.approvalLogs[ticket.data.approvalLogs.length - 1]?.employee.id === userInfo?.id && (
            <Popconfirm
              title={'Bạn chắc chắn thu hổi phê duyệt?'}
              onConfirm={() =>
                handleCancelApproval(
                  ticket.data.id,
                  ticket.data.approvalLogs[ticket.data.approvalLogs.length - 1]?.step
                )
              }
            >
              <BtnCustom
                type='cancelApproval'
                stepCancelApproval={ticket.data.approvalLogs[ticket.data.approvalLogs.length - 1]?.step}
                isLoading={cancelApprovalTicketsMutation.isLoading}
              />
            </Popconfirm>
          )}
        </Flex>
      );
    }
    //#endregion Ticket approval

    //#region Ticket accept
    if (
      basePath === urlPath.accept.location &&
      ticket?.data.ticketStatusId === TicketStatus.ApprovedRequest &&
      ticket.data.permissionAccept
    ) {
      return (
        <Flex gap={10}>
          <Popconfirm
            title={'Bạn chắc chắn tiếp nhận?'}
            onConfirm={() => {
              acceptTicketsMutation.mutate({
                id: String(ticket?.data.id),
                action: ApprovalStatus.Approve
              });
            }}
          >
            <BtnCustom
              type='accept'
              isLoading={
                acceptTicketsMutation.isLoading && acceptTicketsMutation.variables?.action === ApprovalStatus.Approve
              }
            />
          </Popconfirm>
          <BtnCustom
            type='reject'
            onClick={() => setOpenRejectNote({ isOpen: true, processType: 'accept' })}
            isLoading={
              acceptTicketsMutation.isLoading && acceptTicketsMutation.variables?.action === ApprovalStatus.Reject
            }
          />
        </Flex>
      );
    }
    //#endregion Ticket accept

    //#region Ticket handle
    if (basePath === urlPath.solution.location && ticket?.data.ticketStatusId === TicketStatus.AcceptReceiveRequest) {
      if (ticket.data.permissionAssign || ticket.data.permissionApproveSolution) {
        return (
          <Flex gap={10}>
            <BtnCustom
              type='handle'
              onClick={() => {
                setOpenHandle({
                  isOpen: true,
                  departmentId: ticket.data?.departmentHandleId,
                  ticketId: ticket.data?.id,
                  ticketCode: ticket.data?.ticketCode,
                  employeeId: ticket.data?.handlerObj?.id,
                  solutionTicketId: ticket.data?.ticketSolutions?.find((item) => item.isApproval)?.id
                });
              }}
            />
            {ticket.data?.ticketSolutions && ticket.data?.ticketSolutions?.length > 0 ? (
              <BtnCustom
                type='updateSolution'
                onClick={() => {
                  navigate(`${urlPath.solutionAdd.location}`);
                }}
              />
            ) : (
              <BtnCustom
                type='addSolution'
                onClick={() => {
                  navigate(`${urlPath.solutionAdd.location}`);
                }}
              />
            )}
          </Flex>
        );
      } else {
        return ticket.data?.ticketSolutions && ticket.data?.ticketSolutions?.length > 0 ? (
          <BtnCustom
            type='updateSolution'
            onClick={() => {
              navigate(`${urlPath.solutionAdd.location}`);
            }}
          />
        ) : (
          <BtnCustom
            type='addSolution'
            onClick={() => {
              navigate(`${urlPath.solutionAdd.location}`);
            }}
          />
        );
      }
    }

    if (
      basePath === urlPath.solution.location &&
      ticket?.data.ticketStatusId === TicketStatus.Processing &&
      ticket.data.countNotCompleted > 0
    ) {
      if (ticket.data.permissionAssign || ticket.data.permissionApproveSolution) {
        return (
          <Flex gap={10}>
            <BtnCustom
              type='handle'
              onClick={() => {
                setOpenHandle({
                  isOpen: true,
                  departmentId: ticket.data?.departmentHandleId,
                  ticketId: ticket.data?.id,
                  ticketCode: ticket.data?.ticketCode,
                  employeeId: ticket.data?.handlerObj?.id,
                  solutionTicketId: ticket.data?.ticketSolutions?.find((item) => item.isApproval)?.id
                });
              }}
            />
            {ticket.data?.ticketSolutions && ticket.data?.ticketSolutions?.length > 0 ? (
              <BtnCustom
                type='updateSolution'
                onClick={() => {
                  navigate(`${urlPath.solutionAdd.location}`);
                }}
              />
            ) : (
              <BtnCustom
                type='addSolution'
                onClick={() => {
                  navigate(`${urlPath.solutionAdd.location}`);
                }}
              />
            )}
          </Flex>
        );
      } else {
        return ticket.data?.ticketSolutions && ticket.data?.ticketSolutions?.length > 0 ? (
          <BtnCustom
            type='updateSolution'
            onClick={() => {
              navigate(`${urlPath.solutionAdd.location}`);
            }}
          />
        ) : (
          <BtnCustom
            type='addSolution'
            onClick={() => {
              navigate(`${urlPath.solutionAdd.location}`);
            }}
          />
        );
      }
    }
    //#endregion Ticket handle

    //#region Ticket my handle
    if (basePath === urlPath.handle.location && ticket?.data.ticketStatusId === TicketStatus.Processing) {
      return (
        <Flex gap={10}>
          <BtnCustom
            type='completedHandle'
            onClick={() => setOpenNote({ isOpen: true, processType: 'handle' })}
            isLoading={completedHandleMutation.isLoading}
          />
          <BtnCustom
            type='rejectHandle'
            onClick={() => setOpenRejectNote({ isOpen: true, processType: 'handle' })}
            isLoading={rejectHandleMutation.isLoading}
          />
          {(!isHasRelatedTicket ||
            ticket.data.ticketCategory.ticketCategoryMappings.some(
              (x) => x.hrTicketCategory.code === HrCategoryEn.AWT_PACKAGE
            ) ||
            ticket.data.manpowerSupplyForms.length > 0) && (
            <BtnCustom
              type='addTicketRelate'
              onClick={() =>
                setOpenRelatedTicket({
                  isOpen: true,
                  ticketId: ticket.data.id,
                  manpowerId:
                    ticket.data.ticketCategory.ticketCategoryMappings.some(
                      (x) =>
                        x.hrTicketCategory.code === HrCategoryEn.BT_TICKET ||
                        x.hrTicketCategory.code === HrCategoryEn.OT_TICKET
                    ) && isJsonType(ticket.data.ticketForms[0].value)
                      ? JSON.parse(ticket.data.ticketForms[0].value).id
                      : undefined
                })
              }
            />
          )}
        </Flex>
      );
    }
    //#endregion Ticket my handle
  };

  return (
    <Fragment>
      <Flex className='row-handle' justify='space-between'>
        <Icon url={Close} className='btn-close' size={25} onClick={() => handleCloseDetail()} />
        <Flex className='btn-handle' gap={10}>
          <BtnCustom type='chat' onClick={() => navigate(`${urlPath.topic.location}/${ticket?.data?.topicId}`)} />
          {handleRenderButton()}
        </Flex>
      </Flex>
      {openNote.isOpen && <AddNote open={openNote} setOpen={setOpenNote} handleAddNote={handleAddNote} />}
      {openRejectNote.isOpen && (
        <AddRejectNote open={openRejectNote} setOpen={setOpenRejectNote} handleAddRejectNote={handleAddRejectNote} />
      )}
      {openHandle.isOpen && (
        <HandleTicket open={openHandle} setOpenHandle={setOpenHandle} handleRefresh={handleRefresh} />
      )}
      {openRelatedTicket.isOpen && <AddRelatedTicket setOpen={setOpenRelatedTicket} open={openRelatedTicket} />}
    </Fragment>
  );
};

export default BtnHandleList;
